/* THE UNDO WINDOW, OUTSIDE ANY PAGE (W82 step 4, Fable's bound on the 10-second undo, 2026-09-26): the review's `Send` is a
 * DELAYED insert, and a coach who taps Skip (or ← / →, or any link) by reflex inside the window still has the Undo he was
 * promised — so the pending send lives HERE, a module store with its own timer, and the app-level toast
 * (`PendingSendToast`) follows him to the next page for the seconds that remain. The commit is CAPTURED at Send time (the
 * rows, the next-week writes), so it never depends on the page that made it still being mounted.
 *
 * ONE pending send at a time: starting a second one SENDS the first at once (it was already promised). The clock and the
 * timer are injected so `pendingSend.test.ts` walks the window without waiting. */

export type Pending = { readonly key: string; readonly label: string; readonly deadline: number };
export type Done = { readonly key: string; readonly outcome: string | null; readonly seq: number };
type Timers = { set: (fn: () => void, ms: number) => unknown; clear: (h: unknown) => void; now: () => number };

const real: Timers = { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>), now: () => Date.now() };
let timers: Timers = real;
let entry: (Pending & { commit: () => Promise<string | null>; handle: unknown }) | null = null;
/** The snapshot `useSyncExternalStore` reads — ONE object per state, so React sees a stable value between changes (a fresh
 *  object per read makes it re-render for ever). */
let snapshot: Pending | null = null;
let done: Done | null = null;
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => {
  snapshot = entry === null ? null : { key: entry.key, label: entry.label, deadline: entry.deadline };
  for (const l of listeners) l();
};

/** Tests only: a fake clock and timer. */
export function setTimersForTest(t: Timers | null): void { timers = t ?? real; }

async function fire(e: NonNullable<typeof entry>): Promise<void> {
  if (entry === e) entry = null;
  emit();
  const outcome = await e.commit();
  done = { key: e.key, outcome, seq: ++seq };
  emit();
}

/** Arm a send `ms` from now. A send already pending is sent NOW (it was promised). */
export function startPending(p: { key: string; label: string; ms: number; commit: () => Promise<string | null> }): void {
  if (entry !== null) { const prev = entry; timers.clear(prev.handle); void fire(prev); }
  const e = { key: p.key, label: p.label, deadline: timers.now() + p.ms, commit: p.commit, handle: null as unknown };
  e.handle = timers.set(() => { void fire(e); }, p.ms);
  entry = e;
  emit();
}

/** Undo the pending send (optionally only if it is this key's); true when something was cancelled. */
export function undoPending(key?: string): boolean {
  if (entry === null || (key !== undefined && entry.key !== key)) return false;
  timers.clear(entry.handle);
  entry = null;
  emit();
  return true;
}

export function currentPending(): Pending | null {
  return snapshot;
}
export function lastDone(): Done | null { return done; }
export function subscribePending(fn: () => void): () => void { listeners.add(fn); return () => { listeners.delete(fn); }; }

/** `Sending Dana's review in 7 s`. */
export function pendingWords(p: Pending, now: number): string {
  return `Sending ${p.label} in ${String(Math.max(0, Math.ceil((p.deadline - now) / 1000)))} s`;
}
